import { yupResolver } from '@hookform/resolvers/yup'
import { ErrorMessage } from '@hookform/error-message'
import { Alert, Button, Col, Flex, Form, Input, Row, Select, Typography, notification } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import CommonTag from 'src/app/shared/components/common/tag'
import { ISystemWideRules } from 'src/app/shared/models/systemWideRules.model'
import { getSystemWideRules, updateSystemWideRules } from 'src/app/shared/services/SystemWideRulesService'
import { getLookupValues } from 'src/app/store/masterSetup'
import { colorBorderDisabled, colorTextSubtle } from 'src/app/themes/primaryThemeDefault'
import { inputNumber } from 'src/app/shared/utils'
import {
  formatDataBeforeSubmit,
  generateValidationSchema,
  getCutoffTimeList,
  normalizeCutoffTime,
  SYSTEM_WIDE_RULES_DEFAULTS,
} from './utils'
import './index.scss'

function SystemWideRules() {
  const { t } = useTranslation()
  const [api, contextHolder] = notification.useNotification()
  const lookupValues = useSelector(getLookupValues)
  const { DefaultReason } = lookupValues || {}

  const cutoffTimeList = getCutoffTimeList(15)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<ISystemWideRules>({
    mode: 'all',
    defaultValues: SYSTEM_WIDE_RULES_DEFAULTS,
    resolver: yupResolver(generateValidationSchema(t)) as any,
  })

  const timeZone = useWatch({ control, name: 'timeZone' })

  const getData = useCallback(async () => {
    try {
      const res = await getSystemWideRules()
      if (res?.data) {
        reset({
          ...res.data,
          defaultCheckExceptionCutoffTime: normalizeCutoffTime(res.data?.defaultCheckExceptionCutoffTime),
          defaultAchExceptionCutoffTime: normalizeCutoffTime(res.data?.defaultAchExceptionCutoffTime),
        })
      }
    } catch (error) {
      console.log('ERROR: ', error)
    }
  }, [reset])

  useEffect(() => {
    getData()
  }, [getData])

  const onSubmit = async (data: ISystemWideRules) => {
    try {
      setIsLoading(true)
      await updateSystemWideRules(formatDataBeforeSubmit(data))
      api.success({
        message: t('SystemWideRules:message.saveSuccess'),
        closeIcon: null,
      })
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    getData()
  }

  return (
    <Flex className='system-wide-rules-page'>
      {contextHolder}
      <Form name='system-wide-rules-form' className='system-wide-rules-form-container' layout='vertical'>
        <Typography.Text style={{ color: colorTextSubtle, paddingBottom: '16px' }}>
          {t('SystemWideRules:subtitle')}
        </Typography.Text>

        <Alert
          type='warning'
          showIcon
          style={{ borderRadius: 12, margin: '16px 0px' }}
          message={
            <Typography.Text style={{ fontWeight: 700 }}>{t('SystemWideRules:overrideBanner.title')}</Typography.Text>
          }
          description={<Typography.Text>{t('SystemWideRules:overrideBanner.description')}</Typography.Text>}
        />

        <Flex vertical style={{ paddingBottom: '24px' }}>
          <Typography.Text style={{ fontSize: '18px', fontWeight: '700', lineHeight: '28px' }}>
            {t('SystemWideRules:timeConfiguration.title')}
          </Typography.Text>
          <Typography.Text style={{ color: colorTextSubtle, paddingBottom: '16px' }}>
            {t('SystemWideRules:timeConfiguration.subtitle')}
          </Typography.Text>

          <Flex
            vertical
            style={{ border: `1px solid ${colorBorderDisabled}`, padding: '16px', borderRadius: '16px' }}
          >
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item label={t('SystemWideRules:label.reminderEmailTime')} className='isRequired'>
                  <Controller
                    name='defaultExceptionEmailReminder'
                    control={control}
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        status={!!fieldState.error ? 'error' : ''}
                        onChange={(e) => {
                          field.onChange(inputNumber(e), { shouldValidate: true })
                        }}
                      />
                    )}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='defaultExceptionEmailReminder' />
                  </Typography>
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text style={{ fontSize: '16px', fontWeight: '700', lineHeight: '24px', paddingBottom: '8px' }}>
              {t('SystemWideRules:section.checkExceptions')}
            </Typography.Text>
            <Row gutter={24}>
              <Col span={24} md={12}>
                <Form.Item label={t('SystemWideRules:label.checkExceptionCutoffTime')} className='isRequired'>
                  <Flex gap={8}>
                    <Controller
                      name='defaultCheckExceptionCutoffTime'
                      control={control}
                      render={({ field, fieldState }) => (
                        <Select
                          {...field}
                          status={!!fieldState.error ? 'error' : ''}
                          style={{ flex: 1 }}
                          options={cutoffTimeList}
                        />
                      )}
                    />
                    <Input disabled value={timeZone} style={{ width: 140 }} />
                  </Flex>
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='defaultCheckExceptionCutoffTime' />
                  </Typography>
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text style={{ fontSize: '16px', fontWeight: '700', lineHeight: '24px', paddingBottom: '8px' }}>
              {t('SystemWideRules:section.achExceptions')}
            </Typography.Text>
            <Row gutter={24}>
              <Col span={24} md={12}>
                <Form.Item label={t('SystemWideRules:label.achExceptionCutoffTime')} className='isRequired'>
                  <Flex gap={8}>
                    <Controller
                      name='defaultAchExceptionCutoffTime'
                      control={control}
                      render={({ field, fieldState }) => (
                        <Select
                          {...field}
                          status={!!fieldState.error ? 'error' : ''}
                          style={{ flex: 1 }}
                          options={cutoffTimeList}
                        />
                      )}
                    />
                    <Input disabled value={timeZone} style={{ width: 140 }} />
                  </Flex>
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='defaultAchExceptionCutoffTime' />
                  </Typography>
                </Form.Item>
              </Col>
            </Row>
          </Flex>
        </Flex>

        <Flex className='button-group' justify='flex-end' gap={24}>
          <Button className='cancel-button' onClick={handleCancel}>
            {t('SystemWideRules:button.cancel')}
          </Button>
          <Button
            className='save-button'
            type='primary'
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {t('SystemWideRules:button.save')}
          </Button>
        </Flex>
      </Form>
    </Flex>
  )
}

export default SystemWideRules
